import React, { useEffect, useState, useCallback } from "react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import Marquee from "react-fast-marquee";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, X } from "lucide-react";
import api from "../lib/api";

export default function Home() {
  const [slides, setSlides] = useState([]);
  const [news, setNews] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [viewNews, setViewNews] = useState(null);

  useEffect(() => {
    api.get("/slides").then((r) => setSlides(r.data));
    api.get("/news").then((r) => setNews(r.data));
    api.get("/sponsors").then((r) => setSponsors(r.data));
  }, []);

  return (
    <div data-testid="home-page">
      <Hero slides={slides} />
      <NewsSection news={news} onViewNews={setViewNews} />
      <SponsorsSection sponsors={sponsors} />
      
      {/* News Modal / Detail View */}
      {viewNews && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-5 bg-black/90 backdrop-blur-sm" onClick={() => setViewNews(null)}>
          <div className="relative bg-[#1F1F1F] border border-[#3a3a3a] w-full max-w-4xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <button 
              onClick={() => setViewNews(null)} 
              className="absolute top-4 right-4 z-[110] bg-[#1F1F1F]/80 backdrop-blur-sm border border-[#3a3a3a] text-white p-2 hover:border-[#FFCC00] hover:text-[#FFCC00] transition-colors"
              aria-label="Zamknij"
            >
              <X size={24} />
            </button>
            <div className="overflow-auto flex-1">
              <div className="aspect-video w-full">
                <img src={viewNews.image_url} alt={viewNews.title} className="w-full h-full object-cover" />
              </div>
              <div className="p-8 md:p-12">
                <div className="font-mono-acc text-xs text-[#FFCC00] uppercase mb-4">{viewNews.date}</div>
                <h2 className="font-display text-white text-4xl md:text-6xl uppercase leading-tight mb-8">{viewNews.title}</h2>
                <div className="prose prose-invert prose-yellow max-w-none text-white/80 text-lg leading-relaxed whitespace-pre-wrap">
                  {viewNews.content}
                </div>

                {viewNews.images && viewNews.images.length > 0 && (
                  <div className="mt-12">
                    <div className="kicker mb-6">Galeria zdjęć</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {viewNews.images.map((img, idx) => (
                        <a key={idx} href={img.image_url} target="_blank" rel="noreferrer" className="block border border-[#3a3a3a] overflow-hidden hover:border-[#FFCC00] transition-colors">
                          <img src={img.image_url} alt="" className="w-full h-auto object-cover" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                <button onClick={() => setViewNews(null)} className="mt-12 border border-[#3a3a3a] text-white/70 font-mono-acc text-xs uppercase px-8 py-4 hover:border-[#FFCC00] hover:text-[#FFCC00] transition-colors">
                  Zamknij
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Hero({ slides }) {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true }, [Autoplay({ delay: 6000, stopOnInteraction: false })]);
  const [selected, setSelected] = useState(0);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelected(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on("select", onSelect);
    onSelect();
  }, [emblaApi, onSelect]);

  return (
    <section data-testid="hero-slider" className="relative bg-[#1F1F1F] noise overflow-hidden">
      <div className="overflow-hidden h-[78vh] md:h-[88vh] min-h-[560px]" ref={emblaRef}>
        <div className="flex h-full">
          {slides.map((s) => (
            <div key={s.id} className="relative flex-[0_0_100%] h-full">
              <img src={s.image_url} alt={s.title} className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-black/30" />
              <div className="absolute inset-0 flex items-end">
                <div className="max-w-[1400px] w-full mx-auto px-5 md:px-10 pb-20 md:pb-28">
                  <h1 className={`font-display text-white uppercase max-w-5xl hero-shadow leading-[0.9] ${
                    s.font_size === "small" 
                      ? "text-3xl sm:text-5xl md:text-7xl lg:text-8xl" 
                      : "text-4xl sm:text-6xl md:text-7xl lg:text-[7.5rem]"
                  }`}>
                    {s.title}
                  </h1>
                  {s.subtitle && (
                    <p className="mt-6 text-white/85 text-lg md:text-2xl max-w-2xl hero-shadow">
                      {s.subtitle}
                    </p>
                  )}
                  <div className="mt-10 flex flex-wrap gap-4">
                    <Link to="/warsztaty" className="bg-[#FFCC00] text-black font-display text-sm px-7 py-4 btn-brutal inline-flex items-center gap-2" data-testid="hero-cta-workshops">
                      Zobacz warsztaty <ArrowUpRight size={18}/>
                    </Link>
                    <Link to="/dostep" className="border border-white/30 text-white font-mono-acc text-xs uppercase px-7 py-4 hover:border-[#FFCC00] hover:text-[#FFCC00] transition-colors" data-testid="hero-cta-access">
                      Dostęp do pracowni
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="absolute bottom-8 left-0 right-0 z-20">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 flex items-center gap-2">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => emblaApi && emblaApi.scrollTo(i)}
              className={`dot ${i === selected ? "active" : ""}`}
              data-testid={`hero-dot-${i}`}
              aria-label={`Slajd ${i + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function NewsSection({ news, onViewNews }) {
  const [limit, setLimit] = useState(5);
  const hasMore = news.length > limit;

  return (
    <section data-testid="news-section" className="bg-[#2A2A2A] py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
          <div>
            <div className="kicker mb-4">Aktualności</div>
            <h2 className="font-display text-white text-4xl md:text-6xl uppercase leading-[0.95]">
              Co dzieje się<br/>w pracowni
            </h2>
          </div>
          <p className="text-white/60 max-w-md md:text-right">
            Newsy, otwarte dni, hackathony, wystawy. Bądź na bieżąco z tym, co tworzymy w Śląskim Maker Space.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {news.slice(0, limit).map((n, i) => (
            <article
              key={n.id}
              className={`tile relative group bg-[#1F1F1F] border border-[#3a3a3a] overflow-hidden ${
                i === 0 ? "md:col-span-2 md:row-span-2" : ""
              }`}
              data-testid={`news-card-${i}`}
            >
              <div className={`overflow-hidden ${i === 0 ? "aspect-[16/10]" : "aspect-[4/3]"}`}>
                <img src={n.image_url} alt={n.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </div>
              <div className="p-6">
                <div className="font-mono-acc text-[10px] text-[#FFCC00] uppercase mb-3">
                  {n.date}
                </div>
                <h3 className={`font-display text-white uppercase leading-tight ${i === 0 ? "text-3xl md:text-4xl" : "text-xl"}`}>
                  {n.title}
                </h3>
                {n.excerpt && (
                  <p className="mt-3 text-white/65 text-sm">{n.excerpt}</p>
                )}
                {(n.content || (n.images && n.images.length > 0)) && (
                  <div className="mt-5 inline-flex items-center gap-2 text-[#FFCC00] text-xs font-mono-acc uppercase cursor-pointer hover:underline" onClick={() => onViewNews(n)}>
                    Czytaj <ArrowRight size={14}/>
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>

        {hasMore && (
          <div className="mt-16 text-center">
            <button 
              onClick={() => setLimit(prev => prev + 6)}
              className="border border-[#3a3a3a] text-white font-mono-acc text-xs uppercase px-10 py-5 hover:border-[#FFCC00] hover:text-[#FFCC00] transition-colors"
            >
              Więcej aktualności
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

function SponsorsSection({ sponsors }) {
  if (!sponsors.length) return null;
  return (
    <section data-testid="sponsors-section" className="bg-[#1F1F1F] border-t border-[#3a3a3a] py-16">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 mb-8">
        <div className="kicker">Partnerzy & Sponsorzy</div>
      </div>
      <Marquee gradient={false} speed={40} pauseOnHover>
        {sponsors.map((s) => (
          <div className="marquee-item" key={s.id}>
            <img src={s.image_url} alt={s.name} className="sponsor-logo h-14 md:h-16 object-contain" />
          </div>
        ))}
      </Marquee>
    </section>
  );
}
